import { Editor } from '@tiptap/core'
import { TextSelection } from '@tiptap/pm/state'
import { OutlineItem } from '../../../calls.service'
import { SentenceIdPair } from './ai-action.types'

export function getSelectedText(editor: Editor): string | null {
  const { from, to } = editor.state.selection

  if (from === to) {
    return null
  }

  return editor.state.doc.textBetween(from, to, ' ')
}

export function getSentenceIdPairsFromSelection(editor: Editor, outlineItems: OutlineItem[]): SentenceIdPair[] {
  const { from, to } = editor.state.selection
  const outlineItemsByNodeId = new Map(outlineItems.map((item) => [item.id.toString(), item]))

  const pairs: SentenceIdPair[] = []
  const seen = new Set<string>()

  editor.state.doc.nodesBetween(from, to, (node) => {
    if (node.type.name === 'listItem' && node.attrs['nodeId'] !== null) {
      const outlineItem = outlineItemsByNodeId.get(node.attrs['nodeId']) ?? null

      if (outlineItem !== null) {
        const key = `${outlineItem.startSentenceId}:${outlineItem.endSentenceId}`

        if (!seen.has(key)) {
          seen.add(key)
          pairs.push({
            startSentenceId: outlineItem.startSentenceId,
            endSentenceId: outlineItem.endSentenceId,
          })
        }
      }
    }
  })

  return pairs
}

export function getTemplateItemIdsFromSelection(editor: Editor): number[] {
  const { from, to } = editor.state.selection
  const templateItemIds = new Set<number>()

  editor.state.doc.nodesBetween(from, to, (node) => {
    if (node.type.name === 'listItem' && node.attrs['templateItemId'] !== null) {
      const parsed = Number(node.attrs['templateItemId'])

      if (!Number.isNaN(parsed)) {
        templateItemIds.add(parsed)
      }
    }
  })

  return [...templateItemIds]
}

export function expandSelectionToWordBoundaries(editor: Editor) {
  const { from, to } = editor.state.selection

  if (from === to) {
    throw new Error('The selection is empty.')
  }

  const isWordCharacter = (char: string) => /[\w'\-.!?]/.test(char)

  let expandedFrom = from
  let expandedTo = to

  while (expandedFrom > 0) {
    const char = editor.state.doc.textBetween(expandedFrom - 1, expandedFrom)
    if (char === '' || !isWordCharacter(char)) {
      break
    }
    expandedFrom--
  }

  const docSize = editor.state.doc.content.size
  while (expandedTo < docSize) {
    const char = editor.state.doc.textBetween(expandedTo, expandedTo + 1)
    if (char === '' || !isWordCharacter(char)) {
      break
    }
    expandedTo++
  }

  if (expandedFrom !== from || expandedTo !== to) {
    const transaction = editor.state.tr.setSelection(TextSelection.create(editor.state.doc, expandedFrom, expandedTo))
    editor.view.dispatch(transaction)
  }
}
